import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { User, ChevronLeft, ChevronRight, ExternalLink, AlertTriangle, Info, ArrowUpDown, ArrowUp, ArrowDown, FileText } from 'lucide-react';
import Modal from './Modal';
import { salesCyclesApi } from '../services/api';
import { handleCompanyNameClick, openHubSpotDeal, openCompanyDeals } from '../utils/hubspot';

interface OpenCyclesListModalProps {
  isOpen: boolean;
  onClose: () => void;
  periodLabel: string;
  periodDate?: string | null;
  period?: 'week' | 'month' | 'quarter'; // Add period type for correct date truncation
  category: 'newly_originated' | 'old_with_touchbase' | 'old_without_touchbase' | 'all';
  categoryDisplayName: string;
  selectedCycleTypes?: string[];
  selectedAccountValues?: string[];
  selectedMeetingDispositions?: string[];
  selectedSalesRep?: string | null;
  selectedTeam?: string | null;
}

const OpenCyclesListModal: React.FC<OpenCyclesListModalProps> = ({
  isOpen,
  onClose,
  periodLabel,
  periodDate,
  period = 'week', // Default to week if not provided
  category,
  categoryDisplayName,
  selectedCycleTypes,
  selectedAccountValues,
  selectedMeetingDispositions,
  selectedSalesRep,
  selectedTeam
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showTooltip, setShowTooltip] = useState(false);
  // Sorting is only available when periodDate exists (trends page)
  const [sortField, setSortField] = useState<string>('start_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc'); // Changed to 'asc' for oldest-first sorting
  const sortingEnabled = !!periodDate; // Only enable sorting for time-based views

  // Reset pagination when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen, category]);

  // Convert cycle types array to comma-separated string for API calls
  const cycleTypesString = selectedCycleTypes && selectedCycleTypes.length > 0 ? selectedCycleTypes.join(',') : undefined;

  // Parse meeting dispositions to extract qualification type if present
  // Combined values like TRANSACTIONAL_DEAL need to be split into disposition + qualification_type
  const parseDispositions = (dispositions?: string[]): { meetingDispositions: string[], qualificationType: 'DEAL' | 'ZERO' | null } => {
    if (!dispositions || dispositions.length === 0) {
      return { meetingDispositions: [], qualificationType: null };
    }

    const parsedDispositions: string[] = [];
    // Track which qualification types are selected for each disposition
    const qualTypes: Set<string> = new Set();
    // Track if any disposition without qualification type variants is selected
    // (e.g., TRANSACTIONAL without _DEAL/_ZERO suffix, or NURTURE, BAD_FIT, etc.)
    let hasNonQualTypedDisposition = false;

    for (const d of dispositions) {
      if (d === 'TRANSACTIONAL_DEAL') {
        parsedDispositions.push('TRANSACTIONAL');
        qualTypes.add('DEAL');
      } else if (d === 'TRANSACTIONAL_ZERO') {
        parsedDispositions.push('TRANSACTIONAL');
        qualTypes.add('ZERO');
      } else if (d === 'ENTERPRISE_DEAL') {
        parsedDispositions.push('ENTERPRISE');
        qualTypes.add('DEAL');
      } else if (d === 'ENTERPRISE_ZERO') {
        parsedDispositions.push('ENTERPRISE');
        qualTypes.add('ZERO');
      } else {
        // Regular disposition without qualification type variant
        // (TRANSACTIONAL, ENTERPRISE, NURTURE, BAD_FIT, TRASH, NOT_ANALYZED, etc.)
        parsedDispositions.push(d);
        hasNonQualTypedDisposition = true;
      }
    }

    // Remove duplicates
    const uniqueDispositions = Array.from(new Set(parsedDispositions));

    // Only apply qualification type filter if:
    // 1. Exactly one qualification type is selected (DEAL or ZERO)
    // 2. AND no non-qual-typed dispositions are selected (which would need all qualification types)
    let qualType: 'DEAL' | 'ZERO' | null = null;
    if (qualTypes.size === 1 && !hasNonQualTypedDisposition) {
      qualType = qualTypes.has('DEAL') ? 'DEAL' : 'ZERO';
    }
    // If qualTypes.size === 0 or qualTypes.size === 2 or hasNonQualTypedDisposition, qualType stays null (no filtering)

    return { meetingDispositions: uniqueDispositions, qualificationType: qualType };
  };

  const { meetingDispositions: parsedMeetingDispositions, qualificationType } = parseDispositions(selectedMeetingDispositions);

  // Use different endpoints based on whether we have a periodDate
  // - With periodDate: use getOpenCyclesByActivity (for trends page - time-based)
  // - Without periodDate: use getOpenCyclesByCategory (for dashboard - current status)
  const { data, isLoading, error } = useQuery(
    [
      periodDate ? 'openCyclesNoDealsByActivity' : 'openCyclesByCategory',
      category,
      currentPage,
      pageSize,
      periodDate,
      period,
      cycleTypesString || 'all',
      selectedAccountValues?.join(','),
      parsedMeetingDispositions?.join(','),
      qualificationType,
      selectedSalesRep,
      selectedTeam,
      sortField,
      sortDirection
    ],
    () => {
      if (periodDate) {
        // Time-based view (Open Cycles Trends page)
        return salesCyclesApi.getOpenCyclesByActivity(
          category,
          currentPage,
          pageSize,
          periodDate,
          period,
          cycleTypesString,
          selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
          selectedSalesRep,
          selectedTeam,
          sortField,
          sortDirection,
          parsedMeetingDispositions.length > 0 ? parsedMeetingDispositions : undefined,
          qualificationType
        );
      } else {
        // Current status view (Dashboard)
        // Map category to the format expected by getOpenCyclesByCategory
        const categoryMap: Record<string, string> = {
          'all': 'all',
          'newly_originated': 'all', // No equivalent in category endpoint
          'old_with_touchbase': 'all',
          'old_without_touchbase': 'all',
          // Pass through qualification categories directly (Conflict removed)
          'NURTURE': 'NURTURE',
          'ENTERPRISE': 'ENTERPRISE',
          'TRANSACTIONAL_DEAL': 'TRANSACTIONAL_DEAL',
          'TRANSACTIONAL_ZERO': 'TRANSACTIONAL_ZERO',
          'BAD_FIT': 'BAD_FIT',
          'TRASH': 'TRASH'
        };
        return salesCyclesApi.getOpenCyclesByCategory(
          categoryMap[category] || category, // Use category directly if not in map
          currentPage,
          pageSize,
          cycleTypesString,
          selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
          selectedSalesRep,
          selectedTeam,
          qualificationType || undefined, // qualificationType from parsed dispositions
          undefined, // actionType
          parsedMeetingDispositions.length > 0 ? parsedMeetingDispositions : undefined
        );
      }
    },
    {
      enabled: isOpen,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  const cycles = data?.data || [];
  const pagination = data?.pagination || {
    current_page: 1,
    page_size: pageSize,
    total_deals: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false
  };

  const handleSort = (field: string) => {
    // Only allow sorting when periodDate exists (trends page)
    if (!sortingEnabled) return;

    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-gray-400" />;
    }
    return sortDirection === 'asc' ?
      <ArrowUp className="h-3 w-3 text-purple-600" /> :
      <ArrowDown className="h-3 w-3 text-purple-600" />;
  };

  const getQualificationBadge = (qualification: string | null, hasAnalysisRecord?: boolean, hasMeetingContent?: boolean) => {
    // If no qualification value - distinguish between different states
    if (!qualification) {
      // First check if there's no meeting content (no event_content data)
      if (hasMeetingContent === false) {
        // No meeting content - cycle has no analyzable meeting data
        return <span className="text-xs text-gray-400 italic font-inter">No meetings/emails recorded</span>;
      }
      // Check if analysis record exists
      if (hasAnalysisRecord === false) {
        // No analysis record - cycle hasn't been analyzed yet
        return <span className="text-xs text-gray-400 italic font-inter">Not analyzed</span>;
      } else if (hasAnalysisRecord === true) {
        // Has analysis record but NULL qualification - analysis failed or incomplete
        return <span className="text-xs text-gray-400 italic font-inter">No qualification</span>;
      } else {
        // Unknown state (shouldn't happen but handle gracefully)
        return <span className="text-xs text-gray-400 italic font-inter">Not analyzed</span>;
      }
    }

    const badges: Record<string, { color: string; label: string }> = {
      'TRANSACTIONAL': { color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300', label: 'Transactional' },
      'ENTERPRISE': { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', label: 'Enterprise' },
      'NURTURE': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', label: 'Nurture' },
      'BAD_FIT': { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: 'Bad Fit' },
      'TRASH': { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: 'Trash' }
    };

    const badge = badges[qualification];
    if (badge) {
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium font-inter ${badge.color}`}>
          {badge.label}
        </span>
      );
    }

    return <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded text-xs font-medium font-inter">{qualification}</span>;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${periodLabel} - ${categoryDisplayName}`}
      maxWidth="max-w-[95vw]"
      initialHeight="calc(100vh - 80px)"
    >
      {/* Error state */}
      {error ? (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <div className="text-red-600 dark:text-red-400 font-medium font-inter">Error loading sales cycles</div>
          </div>
          <div className="text-red-500 dark:text-red-400 text-sm mt-1 font-inter">
            Unable to fetch sales cycles data. Please try again later.
          </div>
        </div>
      ) : null}

      {/* Sales Cycles Table Section - Flex-grow to fill available space */}
      <div className="overflow-y-auto flex-1 min-h-[400px]">
        {isLoading ? (
          // Loading state
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">
              Loading {categoryDisplayName.toLowerCase()}...
            </div>
          </div>
        ) : !cycles || cycles.length === 0 ? (
          // Empty state
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
            <div className="text-gray-900 dark:text-gray-100 text-lg font-inter">
              No sales cycles found for {categoryDisplayName.toLowerCase()}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-2 font-inter">
              This category currently has no matching sales cycles.
            </div>
          </div>
        ) : (
          // Table layout
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr>
                <th
                  scope="col"
                  className={`px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('company_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Company</span>
                    {sortingEnabled && renderSortIcon('company_name')}
                  </div>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('cycle_status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortingEnabled && renderSortIcon('cycle_status')}
                  </div>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('company_has_deal')}
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Has Deal</span>
                    {sortingEnabled && renderSortIcon('company_has_deal')}
                  </div>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('recommended_deal_qualification')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Qualification</span>
                    {sortingEnabled && renderSortIcon('recommended_deal_qualification')}
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter"
                >
                  <span>Reason</span>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('cycle_owner_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Owner</span>
                    {sortingEnabled && renderSortIcon('cycle_owner_name')}
                  </div>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('cycle_owner_team')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Team</span>
                    {sortingEnabled && renderSortIcon('cycle_owner_team')}
                  </div>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('account_tier')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Tier</span>
                    {sortingEnabled && renderSortIcon('account_tier')}
                  </div>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('cycle_type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    {sortingEnabled && renderSortIcon('cycle_type')}
                  </div>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('start_date')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Start</span>
                    {sortingEnabled && renderSortIcon('start_date')}
                  </div>
                </th>
                <th
                  scope="col"
                  className={`px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tight font-inter ${sortingEnabled ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                  onClick={() => sortingEnabled && handleSort('content_count')}
                >
                  <div className="flex items-center justify-center space-x-1 relative">
                    <span>Content</span>
                    {sortingEnabled && renderSortIcon('content_count')}
                    <div className="relative">
                      <Info
                        className="h-3 w-3 text-gray-400 cursor-help hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                      />
                      {showTooltip && (
                        <div className="fixed bg-gray-700 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap" style={{ marginTop: '6px', marginLeft: '-60px', zIndex: 10000 }}>
                          Meetings with transcripts
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {cycles.map((cycle: any, index: number) => (
                <tr
                  key={cycle.sales_cycle_id || `cycle-${index}`}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {/* Company Name */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="flex items-center space-x-1.5">
                      <div
                        className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center space-x-1 font-inter"
                        onClick={() => handleCompanyNameClick(
                          undefined,
                          cycle.sales_cycle_id,
                          cycle.company_name
                        )}
                        title="Open Sales Cycle in HubSpot (opens in new tab)"
                      >
                        <span className="truncate max-w-[180px]">{cycle.company_name || 'Unknown Company'}</span>
                        {cycle.sales_cycle_id && (
                          <ExternalLink className="h-3 w-3 text-purple-600 hover:text-purple-700 flex-shrink-0" />
                        )}
                      </div>
                      {/* Deal indicator - subtle icon */}
                      {cycle.has_deal && cycle.deal_id && (
                        <div
                          className="cursor-pointer group flex-shrink-0"
                          onClick={() => openHubSpotDeal(cycle.deal_id)}
                          title="Open Deal in HubSpot (opens in new tab)"
                        >
                          <FileText className="h-3 w-3 text-purple-400 hover:text-purple-600 transition-colors" />
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Cycle Status - Shows actual Databricks status */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    {cycle.cycle_status && (
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium font-inter whitespace-nowrap ${
                        cycle.cycle_status === 'OPEN_WITH_DEAL' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                        cycle.cycle_status === 'OPEN_NO_DEAL' ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                        cycle.cycle_status === 'CLOSED_WON' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                        cycle.cycle_status === 'CLOSED_LOST' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                        cycle.cycle_status === 'CLOSED_NO_DEAL' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {cycle.cycle_status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())}
                      </span>
                    )}
                  </td>

                  {/* Company Has Deal - does this company have ANY open deal in HubSpot? */}
                  <td className="px-2 py-2 whitespace-nowrap text-center">
                    {cycle.company_has_deal ? (
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium font-inter bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 cursor-pointer hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                        onClick={() => cycle.company_id && openCompanyDeals(cycle.company_id)}
                        title="Open deals list in HubSpot"
                      >
                        Yes
                        <ExternalLink className="w-3 h-3" />
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium font-inter bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                        No
                      </span>
                    )}
                  </td>

                  {/* Qualification */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    {getQualificationBadge(cycle.recommended_deal_qualification, cycle.has_analysis_record, cycle.has_meeting_content)}
                  </td>

                  {/* Qualification Reason - 3 lines with full text on hover */}
                  <td className="px-2 py-2 max-w-md">
                    <div
                      className="text-xs text-gray-600 dark:text-gray-400 font-inter line-clamp-3 leading-relaxed"
                      title={cycle.qualification_reason || 'No reason provided'}
                    >
                      {cycle.qualification_reason || '-'}
                    </div>
                  </td>

                  {/* Owner */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-900 dark:text-gray-100 font-inter truncate max-w-[120px]">{cycle.cycle_owner_name || 'Unassigned'}</span>
                    </div>
                  </td>

                  {/* Team */}
                  <td className="px-2 py-2">
                    <span className="text-xs text-gray-900 dark:text-gray-100 font-inter max-w-[80px] inline-block leading-tight">{cycle.cycle_owner_team || '-'}</span>
                  </td>

                  {/* Account Tier */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className="text-xs text-gray-900 dark:text-gray-100 font-inter">{cycle.account_tier || '-'}</span>
                  </td>

                  {/* Cycle Type */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className="text-xs text-gray-900 dark:text-gray-100 font-inter">
                      {cycle.cycle_type === 'NEWBUSINESS' ? 'New Business' : cycle.cycle_type === 'EXPANSION' ? 'Expansion' : cycle.cycle_type || '-'}
                    </span>
                  </td>

                  {/* Start Date */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 font-inter">
                    {cycle.start_date ? new Date(cycle.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '-'}
                  </td>

                  {/* Content Count (transcripts + emails) */}
                  <td className="px-2 py-2 whitespace-nowrap text-center">
                    {cycle.has_meeting_content === false ? (
                      <span className="text-sm text-gray-400 font-inter">-</span>
                    ) : (
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 font-inter">{cycle.content_count || 0}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls - Always show at bottom */}
      <div className="flex items-center justify-between mt-4 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="flex items-center space-x-4">
          <span className="text-gray-700 dark:text-gray-300 text-sm font-inter">
            {isLoading ? (
              'Loading...'
            ) : cycles && cycles.length > 0 ? (
              <>Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, pagination.total_deals)} of {pagination.total_deals} sales cycles</>
            ) : (
              'No sales cycles to display'
            )}
          </span>
          <div className="flex items-center space-x-2">
            <label className="text-gray-700 dark:text-gray-300 text-sm font-inter">Items per page:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 font-inter"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || isLoading}
            className="flex items-center space-x-1 px-3 py-1 rounded bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-inter"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="text-sm">Previous</span>
          </button>

          <span className="text-gray-700 dark:text-gray-300 text-sm px-3 font-inter">
            Page {currentPage} of {pagination.total_pages}
          </span>

          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={currentPage >= pagination.total_pages || isLoading}
            className="flex items-center space-x-1 px-3 py-1 rounded bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-inter"
          >
            <span className="text-sm">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OpenCyclesListModal;
